import { useState } from "react";
import {
  Button,
  Card,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { map } from "lodash";
import { StepType } from "../../../../../gql/graphql";
import { StatusBadge } from "../../../components/StatusBadge";

const descriptions: Record<
  StepType,
  { title: string; description: string; locked?: boolean }
> = {
  SCORE: {
    title: "Score",
    description:
      "Participants are given a score after each match. The winner is the one with the highest score.",
  },
  ROUND_ROBIN: {
    title: "Round robin",
    description:
      "Each participant plays against every other participant once...",
  },
  SINGLE_ELIM: {
    title: "Single elimination",
    description:
      "Participants face off in matches where the loser is immediately eliminated...",
  },
  DOUBLE_ELIM: {
    title: "Double elimination",
    description:
      "Participants are given a second chance after their first loss...",
  },
  CUSTOM: {
    title: "Custom",
    description: "You can create your own rules and scoring system.",
  },
};

interface StepTypeSelectorProps {
  onSelect: (type: StepType) => void;
}

export const StepTypeSelector = ({ onSelect }: StepTypeSelectorProps) => {
  const [userSelectedStepType, setUserSelectedStepType] =
    useState<StepType | null>(null);
  const mantineTheme = useMantineTheme();

  return (
    <Stack>
      <Title>Select a step type</Title>
      <SimpleGrid cols={4}>
        {map(
          descriptions,
          ({ title, description, locked }, stepType: StepType) => (
            <Card
              key={stepType}
              withBorder={true}
              className={locked ? undefined : "wp-card-interactive"}
              onClick={() => {
                if (locked) return;
                setUserSelectedStepType(stepType);
              }}
              style={{
                cursor: locked ? "not-allowed" : "pointer",
                opacity: locked ? 0.5 : 1,
                borderColor:
                  userSelectedStepType === stepType
                    ? mantineTheme.colors.cyan[7]
                    : undefined,
              }}
            >
              <Stack gap="xs">
                <Text fw={500}>{title}</Text>
                {locked && <StatusBadge status="Coming Soon" />}
                <ScrollArea h={100}>
                  <Text size="sm" c="dimmed">
                    {description}
                  </Text>
                </ScrollArea>
              </Stack>
            </Card>
          ),
        )}
      </SimpleGrid>
      <Button
        disabled={!userSelectedStepType}
        onClick={() => {
          if (userSelectedStepType) {
            onSelect(userSelectedStepType);
          }
        }}
      >
        Continue
      </Button>
    </Stack>
  );
};
